import i18n from "@/i18n";
import { CreditVueGB } from "./CreditVueGB";
import  { CreditRequestStates } from "fs-pb-vue2/data/enums/creditRequestStates";
import IAlertService from "fs-pb-vue2/services/alert/IAlertService";
import { container } from "@/inversify.config";
import { DiTokens } from "fs-pb-vue2/services/diTokens";
import { OperationsApi } from "fs-pb-vue2/services/api/OperationsApi";
import { OperationRequestData } from "fs-pb-vue2/data/operationRequestData";
import { DeclarationPropertiesTypes } from "fs-pb-vue2/data/enums/declarationPropertiesTypes";
import { ContractModel } from "fs-pb-vue2/models/contracts/contractModel";
import { CardModel } from "fs-pb-vue2/models/contracts/cardModel";
import { ContractsApi } from "fs-pb-vue2/services/api/ContractsApi";

/** Модель представления счета */
export class CreditRequestVueGB extends CreditVueGB {

  // отображение окна отмены заявки
  showCancelModal: boolean = false;

  // Индикатор загрузки
  isLoading: boolean = false;

  // сервис уведомлений
  get alertService(): IAlertService {
    return container.get<IAlertService>(DiTokens.AlertService);
  }

  // Описание
  get rem(): string {
    return this.contract.Data.CreditRequestData?.Rem;
  }

  // Сумма кредита
  get summa(): string {
    return this.contract.Data.CreditRequestData?.Summa;
  }

  // Ставка кредита
  get percentRate(): string {
    return this.contract.Data.CreditRequestData?.PercentRate;
  }

  // Срок кредита
  get period(): string {
    return this.contract.Data.CreditRequestData?.Period;
  }

  // Платёж по кредиту
  get payment(): string {
    return this.contract.Data.CreditRequestData?.Payment;
  }

  // Текущий статус заявки Рассмотрение
  get currentRequestStateIsConsideration(): boolean {
    return this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.UnderConsideration;
  }

  // Текущий статус заявки Подготовка документов
  get currentRequestStateIsDocumentPreparation(): boolean {
    return this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.Approved || 
           this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.DocsPreparing;
  }

  // Текущий статус заявки Оформление
  get currentRequestStateIsRegistration(): boolean {
    return this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.DocsPrepared || 
           this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.AwaitToIssued || 
           this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.NeedToOffice;
  }

  // Текущий статус заявки Отклонена
  get currentRequestStateIsDecline(): boolean {
    return this.contract.Data.CreditRequestData?.RequestState == CreditRequestStates.Canceled;
  }

  // Цвет текста описания
  get descriptionColor(): string {
    if (this.contract.Data.CreditRequestData.RequestState == CreditRequestStates.Canceled)
      return 'title-rem-red';
    return 'title-rem';
  }

  // добавление классов статуса Рассмотрение
  get consideration(): boolean {
    return this.currentRequestStateIsConsideration || 
           this.currentRequestStateIsDocumentPreparation || 
           this.currentRequestStateIsRegistration;
  }

  // добавление классов статуса Подготовка документов
  get documentPreparation(): boolean {
    return this.currentRequestStateIsDocumentPreparation || 
           this.currentRequestStateIsRegistration;
  }

  // добавление классов статуса Оформление
  get registration(): boolean {
    return this.currentRequestStateIsRegistration;
  }

  // Кнопка акцепта.
  get acceptText(): string {
    if (this.contract.Data.CreditRequestData.RequestState == CreditRequestStates.Approved) {
      return i18n.t("main.creditRequest.button.docsPreparing").toString();
    } else {
      return i18n.t("main.creditRequest.button.design").toString();
    }
  }

  // Возможность перехода к оформлению кредита
  get canConfirm(): boolean {
    return (
      this.contract.Data.CreditRequestData.RequestState == CreditRequestStates.Approved || 
      this.contract.Data.CreditRequestData.RequestState == CreditRequestStates.DocsPrepared
    ) &&
    this.contract.Data.CreditRequestData.AcceptCatId?.trim() !== '';
  }

  // Видимость поля платежа по кредиту
  get isVisiblePayment(): boolean {
    return this.contract.Data.CreditRequestData.Payment?.trim() !== '';
  }

  // Возможность перехода к оформлению кредита
  get canCancelCredit(): boolean {
    return (
      this.contract.Data.CreditRequestData.RequestState === CreditRequestStates.Approved || 
      this.contract.Data.CreditRequestData.RequestState === CreditRequestStates.DocsPrepared
    ) && this.contract.Data.CreditRequestData.CancelCatId?.trim() !== '';
  }

  // отмена кредита
  async cancelCredit() {
    this.isLoading = true;
    try {
      const cancelCatId = this.contract.Data.CreditRequestData.CancelCatId;
      const referenceId = this.contract.Data.ReferenceId;

      if(!cancelCatId?.trim()) {
        return
      }

      let ord = new OperationRequestData();
      ord.respondentCode = cancelCatId;
      ord.setProperty("Reference", referenceId, DeclarationPropertiesTypes.String);

      ord = await OperationsApi.executeOperationSendAsync(ord);
      this.updateContracts();
      await this.alertService.info( i18n.t("main.creditRequest.canceled").toString());
      this.$router.push('/');

    } catch (e: any) {
      this.alertService.error(e.message);
    } finally {
      this.isLoading = false;
    }
  }

  // подтверждение кредита (открытие операции подготовки документов)
  confirm() {
    const operationCode = Number(this.contract.Data.CreditRequestData.AcceptCatId);

    if (operationCode === 0)
      return

    const addInitData: Record<string, string> = {
      "Reference": this.contract.Data.ReferenceId
    };

    if (this.contract.Data.CreditRequestData.CreditType?.trim()) {
      addInitData["CreditType"] = this.contract.Data.CreditRequestData.CreditType;
    }

    const queryParams = Object.entries(addInitData)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
    .join('&');

    this.$router.push(`/operation/0/${operationCode}?${queryParams}`)
  }

  updateContracts(): void {
    this.contractsStore.addMethod(() => {
  		this.contractsStore.contracts.Contracts.map((contract: ContractModel) => {
        contract.StatementModel.isPeriod = true;
        if (contract.Cards.length > 0) {
          contract.Cards.map(async (card: CardModel) => {
            card.StatementModel.isPeriod = true;
            try {
              const res: any = await ContractsApi.getCardRest(card.Data.Id);
              if (res) {
                for (const key in res) {
                  if (res[key].Properties.SummaCode === 'AvailBalance') {
                    card.Data.Rest = res[key].Properties.CurrentSumma;
                  }
                }
              }
            } catch (e: any) {
              this.alertService.error(e.messsage);
            }
          });
        }
      });
  	});
    this.contractsStore.fetchContracts();
  }
}
